import { gameRules } from './data/gameRules.js';

// DOM Elements
const sidebarSearchInput = document.getElementById('sidebar-search-input');
const tocLinks = document.querySelectorAll('.toc-link, .toc-sub-link');
const rulesTableBody = document.getElementById('rules-table-body');
const rulesCategoryTabs = document.getElementById('rules-category-tabs');
const rulesSearchInput = document.getElementById('rules-search-input');
const backToTopBtn = document.getElementById('back-to-top');
const mobileTocToggle = document.getElementById('mobile-toc-toggle');
const guideSidebar = document.getElementById('guide-sidebar');

// State
let activeRulesCategory = 'all';
let rulesSearchQuery = '';

/**
 * Initializes the Guide page
 */
function init() {
  initScrollSpy();
  initRulesMatrix();
  initSidebarSearch();
  initCopyButtons();
  initMobileToc();
  initBackToTop();
}

/**
 * ScrollSpy: Highlights active TOC link based on viewport scroll position
 */
function initScrollSpy() {
  const sections = document.querySelectorAll('.guide-section, .guide-sub-section');
  if (sections.length === 0) return;

  const observerOptions = {
    root: null,
    rootMargin: '-80px 0px -60% 0px',
    threshold: 0
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const id = entry.target.getAttribute('id');
        if (!id) return;

        // Update active class on matching TOC link
        tocLinks.forEach((link) => {
          const href = link.getAttribute('href');
          if (href === `#${id}`) {
            link.classList.add('active');
          } else {
            link.classList.remove('active');
          }
        });
      }
    });
  }, observerOptions);

  sections.forEach((sec) => observer.observe(sec));
}

/**
 * In-Sidebar Search: Filter TOC items in real time
 */
function initSidebarSearch() {
  if (!sidebarSearchInput) return;

  sidebarSearchInput.addEventListener('input', (e) => {
    const query = e.target.value.toLowerCase().trim();
    const tocItems = document.querySelectorAll('.toc-item');

    tocItems.forEach((item) => {
      const text = item.textContent.toLowerCase();
      if (!query || text.includes(query)) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });
  });
}

/**
 * Interactive UpDog Rules Matrix: Rendering and filtering
 */
function initRulesMatrix() {
  if (!rulesTableBody) return;

  renderRulesCategoryTabs();
  renderRulesTable();

  // Search input event
  if (rulesSearchInput) {
    rulesSearchInput.addEventListener('input', (e) => {
      rulesSearchQuery = e.target.value.toLowerCase().trim();
      renderRulesTable();
    });
  }
}

/**
 * Renders the category filter tabs for UpDog Game Rules
 */
function renderRulesCategoryTabs() {
  if (!rulesCategoryTabs) return;

  const categories = ['all', ...new Set(gameRules.map((r) => r.category))];

  let tabsHtml = '';
  categories.forEach((cat) => {
    const isAll = cat === 'all';
    const label = isAll ? 'All Games (13)' : cat;
    const isActive = activeRulesCategory.toLowerCase() === cat.toLowerCase();

    tabsHtml += `
      <button 
        class="rules-tab ${isActive ? 'active' : ''}" 
        data-category="${escapeHtml(cat)}"
      >
        ${escapeHtml(label)}
      </button>
    `;
  });

  rulesCategoryTabs.innerHTML = tabsHtml;

  rulesCategoryTabs.addEventListener('click', (e) => {
    const tab = e.target.closest('.rules-tab');
    if (!tab) return;

    rulesCategoryTabs.querySelectorAll('.rules-tab').forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');

    activeRulesCategory = tab.dataset.category;
    renderRulesTable();
  });
}

/**
 * Renders the table rows based on category and search query
 */
function renderRulesTable() {
  if (!rulesTableBody) return;

  const filtered = gameRules.filter((game) => {
    const matchCategory =
      activeRulesCategory === 'all' ||
      game.category.toLowerCase() === activeRulesCategory.toLowerCase();

    if (!rulesSearchQuery) return matchCategory;

    const q = rulesSearchQuery;
    const matchSearch =
      game.name.toLowerCase().includes(q) ||
      game.rules.toLowerCase().includes(q) ||
      game.fieldLayout.toLowerCase().includes(q) ||
      game.category.toLowerCase().includes(q) ||
      (game.highlights && game.highlights.some((h) => h.toLowerCase().includes(q)));

    return matchCategory && matchSearch;
  });

  if (filtered.length === 0) {
    rulesTableBody.innerHTML = `
      <tr>
        <td colspan="4" style="text-align: center; padding: 2rem; color: var(--text-muted);">
          No games match your search criteria.
        </td>
      </tr>
    `;
    return;
  }

  const rowsHtml = filtered
    .map((game) => {
      const highlightsHtml = game.highlights
        ? game.highlights.map((h) => `<span class="rules-highlight-tag">${escapeHtml(h)}</span>`).join('')
        : '';

      return `
        <tr>
          <td>
            <div class="rules-game-name">${escapeHtml(game.name)}</div>
            <span style="font-size: 0.76rem; color: var(--text-muted);">${escapeHtml(game.category)}</span>
          </td>
          <td>
            <span class="rules-time-badge">${escapeHtml(game.timeLimit)}</span>
          </td>
          <td style="font-size: 0.88rem;">${escapeHtml(game.fieldLayout)}</td>
          <td>
            <div>${escapeHtml(game.rules)}</div>
            <div style="margin-top: 0.4rem;">${highlightsHtml}</div>
          </td>
        </tr>
      `;
    })
    .join('');

  rulesTableBody.innerHTML = rowsHtml;
}

/**
 * Setup Copy-to-Clipboard buttons for code snippets and headings
 */
function initCopyButtons() {
  // Code block copy buttons
  document.querySelectorAll('.code-copy-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const container = btn.closest('.code-block-container');
      const code = container ? container.querySelector('.code-block-content') : null;
      if (!code) return;

      navigator.clipboard.writeText(code.textContent.trim()).then(() => {
        const originalText = btn.innerHTML;
        btn.innerHTML = `<span>✓ Copied!</span>`;
        setTimeout(() => {
          btn.innerHTML = originalText;
        }, 2000);
      });
    });
  });

  // Heading anchor click copies direct URL
  document.querySelectorAll('.anchor-link').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');
      if (href) {
        const fullUrl = window.location.origin + window.location.pathname + href;
        navigator.clipboard.writeText(fullUrl).catch(() => {});
      }
    });
  });
}

/**
 * Mobile TOC Drawer Toggle
 */
function initMobileToc() {
  if (!mobileTocToggle || !guideSidebar) return;

  mobileTocToggle.addEventListener('click', () => {
    guideSidebar.classList.toggle('open');
    const isOpen = guideSidebar.classList.contains('open');
    mobileTocToggle.innerHTML = isOpen
      ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Close TOC`
      : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg> Table of Contents`;
  });

  // Close sidebar when clicking a link inside on mobile
  tocLinks.forEach((link) => {
    link.addEventListener('click', () => {
      if (window.innerWidth <= 1024) {
        guideSidebar.classList.remove('open');
        mobileTocToggle.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg> Table of Contents`;
      }
    });
  });
}

/**
 * Back to Top Floating Button
 */
function initBackToTop() {
  if (!backToTopBtn) return;

  window.addEventListener('scroll', () => {
    if (window.scrollY > 400) {
      backToTopBtn.classList.add('visible');
    } else {
      backToTopBtn.classList.remove('visible');
    }
  });

  backToTopBtn.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Start
init();
